import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import PageShell from '@components/pages/PageShell';
import { ResourceHero, SectionLabel } from '@components/pages/ResourceSections';
import Newsletter from '@components/landing/Newsletter';
import { PROGRAMS, PROJECTS, primaryProgram, programLabel, type Project, type ProjectStatus } from '@/data/projects';
import Seo from '@components/Seo';
import { Img } from '@/components/Img';

const STATUS_TONE: Record<ProjectStatus, string> = {
    Active: 'bg-[#e8f5ec] text-[#1c6b39]',
    Pilot: 'bg-[#fff0e5] text-[#b34700]',
    Completed: 'bg-[#f0f0f0] text-[#5e5e5e]',
};

function ProjectCard({ project }: { project: Project }) {
    return (
        <Link
            to={`/projects/${project.slug}`}
            className="group flex flex-col overflow-hidden rounded-[10px] bg-[#f5f5f5] transition-shadow duration-300 hover:shadow-[0_18px_40px_-22px_rgba(0,0,0,0.35)]"
        >
            <div className="relative h-[190px] w-full overflow-hidden bg-[#d9d9d9]">
                <Img
                    src={project.image}
                    alt=""
                    className="absolute inset-0 size-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
                />
                <span
                    className={`absolute left-4 top-4 rounded-full px-3 py-1 text-[11px] font-bold ${STATUS_TONE[project.status]}`}
                >
                    {project.status}
                </span>
                <span className="absolute right-4 top-4 flex size-10 items-center justify-center rounded-full bg-white/95 p-2 shadow-[0_4px_14px_-6px_rgba(0,0,0,0.5)]">
                    <Img src={project.logo} alt="" className="size-full object-contain" />
                </span>
            </div>

            <div className="flex flex-1 flex-col p-5">
                <p className="text-[11px] font-bold tracking-[0.4px] text-[#f60] uppercase">
                    {programLabel(primaryProgram(project))}
                    {project.programs.length > 1 && (
                        <span className="text-[#9a9a9a]"> +{project.programs.length - 1}</span>
                    )}
                </p>
                <h3 className="mt-2 text-[17px] font-bold leading-[1.3] text-[#08080c]">{project.name}</h3>
                <p className="mt-2 text-[13px] leading-[1.7] text-[#5e5e5e]">{project.tagline}</p>

                <div className="mt-5 flex items-center justify-between gap-3 border-t border-[#e2e2e2] pt-4">
                    <span className="text-[12px] text-[#5e5e5e]">Since {project.started}</span>
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-full border border-[#d0d0d0] text-[#08080c] transition-colors group-hover:border-[#08080c] group-hover:bg-[#08080c] group-hover:text-white">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                            <path
                                d="M5 12h13M12 5l7 7-7 7"
                                stroke="currentColor"
                                strokeWidth="2.4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </span>
                </div>
            </div>
        </Link>
    );
}

/** Portfolio-level numbers, so the page says something before you click into it. */
function PortfolioStats() {
    const states = new Set(PROJECTS.flatMap((p) => p.coverage.split(/,| and /).map((s) => s.trim()))).size;
    const figures = [
        { value: String(PROJECTS.length), label: 'Projects running' },
        { value: String(PROGRAMS.length), label: 'Programmes' },
        { value: String(PROJECTS.filter((p) => p.status === 'Active').length), label: 'Active today' },
        { value: `${states}+`, label: 'Places we work' },
    ];

    return (
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-[10px] bg-[#e2e2e2] lg:grid-cols-4">
            {figures.map((f) => (
                <div key={f.label} className="bg-white px-5 py-6 text-center lg:py-8">
                    <p className="font-primary text-[30px] font-bold tracking-[-1px] text-[#08080c] lg:text-[38px]">
                        {f.value}
                    </p>
                    <p className="mt-1 text-[12px] text-[#5e5e5e]">{f.label}</p>
                </div>
            ))}
        </div>
    );
}

export default function Projects() {
    const [program, setProgram] = useState('all');

    const shown = useMemo(
        () => (program === 'all' ? PROJECTS : PROJECTS.filter((p) => p.programs.includes(program))),
        [program],
    );

    const tabs = [
        { slug: 'all', label: 'All', count: PROJECTS.length },
        ...PROGRAMS.map((p) => ({
            slug: p.slug,
            label: p.label,
            count: PROJECTS.filter((x) => x.programs.includes(p.slug)).length,
        })),
    ];

    return (
        <PageShell>
            <Seo
                title="Our Projects"
                description="Every piece of work we run, grouped by the programme it belongs to — what it does, who it reaches, and what it has changed so far."
            />
            <ResourceHero
                titleLead="Our"
                titleBold="Projects"
                intro="Every piece of work we run, grouped by the programme it belongs to — what it does, who it reaches, and what it has changed so far."
                search={false}
            />

            <section className="bg-white pb-14 pt-12 lg:pb-20 lg:pt-16">
                <div className="mx-auto max-w-[1640px] px-5 lg:px-[140px]">
                    <PortfolioStats />

                    <div className="mt-12">
                        <SectionLabel>Filter by programme</SectionLabel>

                        {/* Tabs scroll sideways on narrow screens rather than wrapping into a block. */}
                        <div
                            role="tablist"
                            aria-label="Filter projects by programme"
                            className="scrollbar-hide -mx-5 mt-5 flex gap-2 overflow-x-auto px-5 lg:mx-0 lg:flex-wrap lg:px-0"
                        >
                            {tabs.map((t) => {
                                const active = program === t.slug;
                                return (
                                    <button
                                        key={t.slug}
                                        type="button"
                                        role="tab"
                                        aria-selected={active}
                                        onClick={() => setProgram(t.slug)}
                                        className={`flex shrink-0 items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-bold transition-colors ${
                                            active
                                                ? 'bg-[#08080c] text-white'
                                                : 'border border-[#e2e2e2] bg-white text-[#5e5e5e] hover:border-[#08080c] hover:text-[#08080c]'
                                        }`}
                                    >
                                        {t.label}
                                        <span className={active ? 'text-white/60' : 'text-[#9a9a9a]'}>{t.count}</span>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                            {shown.map((p) => (
                                <ProjectCard key={p.slug} project={p} />
                            ))}
                        </div>

                        {shown.length === 0 && (
                            <p className="mt-10 rounded-[10px] border border-dashed border-[#d0d0d0] px-6 py-12 text-center text-[13px] text-[#5e5e5e]">
                                Nothing running under this programme yet.
                            </p>
                        )}
                    </div>
                </div>
            </section>

            <Newsletter />
        </PageShell>
    );
}
